import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast, Toaster } from 'sonner';
import { ArrowLeft, Tv2, Loader2, Radio, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { liveApi, clearViewer, getViewerToken } from './liveClient';
import CloudflareLivePlayer from './CloudflareLivePlayer';

// ── Timing constants ────────────────────────────────────────────────────────
const STATUS_POLL_MS = 10000;      // poll authoritative stream status every 10s
const HEARTBEAT_MS = 30000;        // keep the viewer session alive every 30s
const GRACE_MS = 30 * 60 * 1000;   // 30-min grace before auto-logout once a stream is confirmed unavailable

// Cloudflare playback URL → live-input UID (fallback when cloudflare_uid is absent).
const deriveUid = (playbackUrl) => {
  if (!playbackUrl) return '';
  const m = playbackUrl.match(/cloudflarestream\.com\/([^/]+)\//);
  return m ? m[1] : '';
};

export default function LiveWatchPage() {
  const nav = useNavigate();
  const { streamId } = useParams();
  const wakeLockRef = useRef(null);
  const interruptedSinceRef = useRef(null);  // ms timestamp of first confirmed interruption (null = healthy)
  const hasBeenLiveRef = useRef(false);      // grace logout only applies once a stream has actually gone live
  const endedRef = useRef(false);            // guard so auto-logout fires once

  const [stream, setStream] = useState(null);
  const [status, setStatus] = useState('');          // 'live' | 'scheduled' | 'ended'
  const [uid, setUid] = useState('');
  const [loading, setLoading] = useState(true);
  const [recovering, setRecovering] = useState(false); // reconnecting overlay
  const [waiting, setWaiting] = useState(false);       // scheduled / not-started overlay
  const [graceLeftMs, setGraceLeftMs] = useState(0);   // countdown shown during recovery

  // Redirect unauthenticated users to login, preserving the deep-link.
  useEffect(() => {
    if (!getViewerToken()) {
      nav(`/live/login?next=${encodeURIComponent(`/live/watch/${streamId}`)}`, { replace: true });
    }
  }, [streamId, nav]);

  // ── Wake Lock — keep mobile screens awake during a majlis broadcast ────────
  const requestWakeLock = useCallback(async () => {
    try {
      if ('wakeLock' in navigator && document.visibilityState === 'visible' && !wakeLockRef.current) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        wakeLockRef.current.addEventListener?.('release', () => { wakeLockRef.current = null; });
      }
    } catch { /* wake lock not granted — non-fatal */ }
  }, []);

  const releaseWakeLock = useCallback(async () => {
    try { await wakeLockRef.current?.release(); } catch { /* ignore */ }
    wakeLockRef.current = null;
  }, []);

  // ── Auto-logout (grace expired, stream ended, or access revoked) ───────────
  const autoLogout = useCallback(async (reason) => {
    if (endedRef.current) return;
    endedRef.current = true;
    releaseWakeLock();
    toast.message(
      reason === 'revoked' ? 'Your access to this stream was removed'
      : 'This stream has ended — you are being signed out'
    );
    try { await liveApi().post('/livestream/viewer/logout'); } catch { /* ignore */ }
    clearViewer();
    setTimeout(() => nav('/live/login', { replace: true }), 1500);
  }, [nav, releaseWakeLock]);

  const markInterrupted = useCallback(() => {
    if (interruptedSinceRef.current == null) interruptedSinceRef.current = Date.now();
    setRecovering(true);
  }, []);

  const markRecovered = useCallback(() => {
    interruptedSinceRef.current = null;
    setRecovering(false);
    setGraceLeftMs(0);
  }, []);

  // ── Initial load ───────────────────────────────────────────────────────────
  useEffect(() => {
    endedRef.current = false;
    (async () => {
      try {
        const r = await liveApi().get(`/livestream/viewer/watch/${streamId}`);
        const s = r.data.stream || {};
        setStream(s);
        setStatus(s.status || '');
        setUid(s.cloudflare_uid || deriveUid(s.playback_url));
        setLoading(false);
        if (s.status === 'live') hasBeenLiveRef.current = true;
        requestWakeLock();
      } catch (e) {
        setLoading(false);
        if (e?._kicked) { toast.error(e._friendly); clearViewer(); setTimeout(() => nav('/live/login', { replace: true }), 1500); return; }
        if (e?.response?.status === 403) { autoLogout('revoked'); return; }
        markInterrupted();
      }
    })();
    return () => { releaseWakeLock(); try { liveApi().post('/livestream/viewer/stop-watch'); } catch { /* ignore */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamId]);

  // ── Re-acquire wake lock when the tab returns to the foreground ────────────
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible') requestWakeLock(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [requestWakeLock]);

  // ── Heartbeat every 30s (keeps session alive; surfaces single-session kicks) ─
  useEffect(() => {
    const t = setInterval(async () => {
      try { await liveApi().post('/livestream/viewer/heartbeat'); }
      catch (e) {
        if (e?._kicked) { toast.error(e._friendly || 'Logged in from another device'); clearViewer(); setTimeout(() => nav('/live/login', { replace: true }), 1500); }
      }
    }, HEARTBEAT_MS);
    return () => clearInterval(t);
  }, [nav]);

  // ── Authoritative status poll every 10s (drives recovery + grace logout) ───
  useEffect(() => {
    const evaluateGrace = () => {
      const since = interruptedSinceRef.current;
      if (since == null) { setGraceLeftMs(0); return; }
      const left = GRACE_MS - (Date.now() - since);
      setGraceLeftMs(Math.max(0, left));
      if (left <= 0 && hasBeenLiveRef.current) autoLogout('ended');
    };

    const poll = async () => {
      try {
        const r = await liveApi().get(`/livestream/viewer/status?stream_id=${encodeURIComponent(streamId)}`);
        const data = r.data || {};
        if (data.current_assigned === false) { autoLogout('revoked'); return; }
        const cur = (data.streams || []).find((s) => s.id === streamId) || null;
        const st = cur?.status || 'ended';
        setStatus(st);
        if (cur) {
          setStream((prev) => ({ ...(prev || {}), ...cur }));
          const nextUid = cur.cloudflare_uid || deriveUid(cur.playback_url);
          if (nextUid) setUid(nextUid);
        }

        if (st === 'live') {
          hasBeenLiveRef.current = true;
          setWaiting(false);
          markRecovered();
        } else if (st === 'scheduled' && !hasBeenLiveRef.current) {
          setWaiting(true);
          markRecovered();
        } else {
          if (st === 'ended') hasBeenLiveRef.current = true;
          setWaiting(false);
          markInterrupted();
        }
      } catch (e) {
        if (e?._kicked) { toast.error(e._friendly || 'Logged in from another device'); clearViewer(); setTimeout(() => nav('/live/login', { replace: true }), 1500); return; }
        markInterrupted();
      } finally {
        evaluateGrace();
      }
    };

    poll();
    const t = setInterval(poll, STATUS_POLL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamId, nav]);

  const graceMin = Math.ceil(graceLeftMs / 60000);
  const showPlayer = status === 'live' && !!uid;

  return (
    <div className="min-h-screen bg-black text-white" onContextMenu={(e) => e.preventDefault()}>
      <Toaster richColors position="top-center" />
      <header className="bg-slate-900/95 border-b border-slate-800 px-3 py-2 flex items-center gap-2 sticky top-0 z-10">
        <Button size="sm" variant="ghost" onClick={() => nav('/live/dashboard')} className="text-white hover:bg-slate-800" data-testid="watch-back-btn"><ArrowLeft className="w-4 h-4" /></Button>
        <Radio className="w-4 h-4 text-rose-400" />
        <h1 className="font-semibold truncate flex-1">{stream?.title || 'Loading…'}</h1>
        {status === 'live' && <Badge className="bg-red-600 animate-pulse" data-testid="live-status-badge">🔴 LIVE</Badge>}
      </header>

      <div className="max-w-6xl mx-auto p-3 space-y-3">
        <div className="aspect-video bg-slate-950 rounded-md overflow-hidden relative border border-slate-800">
          {showPlayer && <CloudflareLivePlayer src={uid} />}

          {loading && <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-rose-400" /></div>}

          {/* Waiting (scheduled / not started) */}
          {!loading && !showPlayer && waiting && (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center bg-slate-950/80 z-20" data-testid="waiting-overlay">
              <Tv2 className="w-12 h-12 text-amber-300 mb-3" />
              <div className="text-amber-200 text-sm font-medium">Stream hasn’t started yet</div>
              <div className="text-slate-400 text-xs mt-1">This page will start playing automatically the moment it goes live.</div>
            </div>
          )}

          {/* Reconnecting (interrupted within grace window) */}
          {!loading && !showPlayer && !waiting && (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center bg-slate-950/85 z-20" data-testid="reconnecting-overlay">
              <Loader2 className="w-10 h-10 animate-spin text-amber-300 mb-3" />
              <div className="text-amber-200 text-sm font-medium">Reconnecting to the live stream…</div>
              <div className="text-slate-400 text-xs mt-1">The feed dropped briefly. Retrying automatically — please keep this page open.</div>
              {graceLeftMs > 0 && hasBeenLiveRef.current && recovering && (
                <div className="mt-3 inline-flex items-center gap-1.5 text-amber-300/90 text-xs" data-testid="grace-countdown">
                  <Clock className="w-3.5 h-3.5" /> Auto sign-out in ~{graceMin} min if it doesn’t resume
                </div>
              )}
            </div>
          )}
        </div>

        {stream && (
          <div className="bg-slate-900/60 border border-slate-800 rounded-md p-4 space-y-1">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <h2 className="text-lg font-semibold">{stream.title}</h2>
                {stream.category && <div className="text-xs text-slate-400">{stream.category}</div>}
              </div>
              <div className="text-xs text-slate-400">
                {stream.stream_date && `${stream.stream_date} `}
                {stream.start_time && `${stream.start_time}`}
                {stream.end_time && ` – ${stream.end_time}`}
              </div>
            </div>
            {stream.description && <p className="text-sm text-slate-300 mt-2">{stream.description}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
